import { useId, useState, type FormEvent } from 'react';
import { Trash2 } from 'lucide-react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksToggleGroup } from '../../components/liks/LiksToggleGroup/LiksToggleGroup';
import { useI18n } from '../../i18n/useI18n';
import type { CrawlScheduleFormValues } from '../../features/knowledge/crawlScheduleForm';
import type { CrawlSchedule, ScheduleInterval } from '../../types/knowledge';
import { formatTimestamp as formatTimestampValue } from '../../utils/formatUtils';
import './CrawlScheduleModal.css';

export type { CrawlScheduleFormValues };

const DEFAULT_CUSTOM_INTERVAL_HOURS = 24;

type CrawlScheduleModalProps = {
  crawlStartUrl: string;
  schedule: CrawlSchedule | null;
  onSave: (values: CrawlScheduleFormValues) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onClose: () => void;
  isSubmitting?: boolean;
};

function toFormValues(schedule: CrawlSchedule | null): CrawlScheduleFormValues {
  if (!schedule) {
    return {
      scheduleInterval: 'DAILY',
      customIntervalHours: DEFAULT_CUSTOM_INTERVAL_HOURS,
      active: true,
    };
  }

  return {
    scheduleInterval: schedule.scheduleInterval ?? 'DAILY',
    customIntervalHours:
      schedule.customIntervalHours ?? DEFAULT_CUSTOM_INTERVAL_HOURS,
    active: schedule.active ?? true,
  };
}

export function CrawlScheduleModal({
  crawlStartUrl,
  schedule,
  onSave,
  onDelete,
  onClose,
  isSubmitting = false,
}: CrawlScheduleModalProps) {
  const { translate, locale } = useI18n();
  const customHoursFieldId = useId();
  const isEditMode = schedule != null;
  const [formValues, setFormValues] = useState(() => toFormValues(schedule));
  const [customHoursInput, setCustomHoursInput] = useState(() =>
    String(schedule?.customIntervalHours ?? DEFAULT_CUSTOM_INTERVAL_HOURS),
  );

  const intervalOptions = [
    {
      value: 'DAILY',
      label: translate('knowledge.crawlScheduleInterval.daily'),
    },
    {
      value: 'WEEKLY',
      label: translate('knowledge.crawlScheduleInterval.weekly'),
    },
    {
      value: 'MONTHLY',
      label: translate('knowledge.crawlScheduleInterval.monthly'),
    },
    {
      value: 'CUSTOM',
      label: translate('knowledge.crawlScheduleInterval.custom'),
    },
  ];

  const isCustomInterval = formValues.scheduleInterval === 'CUSTOM';
  const parsedCustomHours =
    customHoursInput.trim() === '' ? null : Number(customHoursInput);
  const isCustomHoursValid =
    !isCustomInterval ||
    (parsedCustomHours != null &&
      Number.isFinite(parsedCustomHours) &&
      parsedCustomHours >= 1);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isCustomHoursValid) {
      return;
    }

    await onSave({
      ...formValues,
      customIntervalHours: isCustomInterval ? parsedCustomHours : null,
    });
  };

  const formatTimestamp = (value: string | null | undefined) =>
    value
      ? formatTimestampValue(value, locale)
      : translate('knowledge.notAvailable');

  return (
    <LiksDialog
      isOpen
      as="form"
      size="lg"
      className="crawl-schedule-modal"
      title={
        <>
          {isEditMode
            ? translate('knowledge.crawlScheduleEditTitle')
            : translate('knowledge.crawlScheduleAddTitle')}
          <span className="crawl-schedule-modal__url">{crawlStartUrl}</span>
        </>
      }
      closeLabel={translate('knowledge.form.close')}
      onClose={onClose}
      onSubmit={handleSubmit}
      isDismissDisabled={isSubmitting}
      closeDisabled={isSubmitting}
      footerClassName="crawl-schedule-modal__footer"
      footer={
        <>
          {isEditMode && onDelete ? (
            <LiksButton
              type="button"
              variant="secondary"
              className="crawl-schedule-modal__delete-button"
              onClick={() => void onDelete()}
              disabled={isSubmitting}
            >
              <Trash2 aria-hidden />{' '}
              {translate('knowledge.crawlScheduleDelete')}
            </LiksButton>
          ) : null}
          <div className="crawl-schedule-modal__primary-actions">
            <LiksButton
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {translate('knowledge.form.cancel')}
            </LiksButton>
            <LiksButton
              type="submit"
              disabled={isSubmitting || !isCustomHoursValid}
            >
              {translate('knowledge.form.save')}
            </LiksButton>
          </div>
        </>
      }
    >
      <div
        className="crawl-schedule-modal__notice"
        role="note"
        aria-label={translate('knowledge.crawlScheduleHowItWorksLabel')}
      >
        <p className="crawl-schedule-modal__notice-title">
          {translate('knowledge.crawlScheduleHowItWorksTitle')}
        </p>
        <ul className="crawl-schedule-modal__notice-list">
          <li>{translate('knowledge.crawlScheduleHowItWorksSelectedUrls')}</li>
          <li>{translate('knowledge.crawlScheduleHowItWorksNoDiscovery')}</li>
          <li>{translate('knowledge.crawlScheduleHowItWorksRediscover')}</li>
          <li>{translate('knowledge.crawlScheduleHowItWorksInterval')}</li>
          <li>{translate('knowledge.crawlScheduleHowItWorksOverlap')}</li>
        </ul>
      </div>

      <LiksToggleGroup
        name="crawl-schedule-interval"
        legend={translate('knowledge.crawlScheduleIntervalLabel')}
        value={formValues.scheduleInterval}
        options={intervalOptions}
        onChange={(value) =>
          setFormValues((current) => ({
            ...current,
            scheduleInterval: value as ScheduleInterval,
          }))
        }
      />

      {isCustomInterval && (
        <label className="crawl-schedule-modal__field">
          <span className="crawl-schedule-modal__label">
            {translate('knowledge.crawlScheduleCustomHoursLabel')}
          </span>
          <input
            id={customHoursFieldId}
            className="crawl-schedule-modal__input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={customHoursInput}
            onChange={(event) => {
              setCustomHoursInput(event.target.value.replace(/\D/g, ''));
            }}
            required
            aria-invalid={!isCustomHoursValid}
          />
          <span className="crawl-schedule-modal__hint">
            {translate('knowledge.crawlScheduleCustomHoursHint')}
          </span>
        </label>
      )}

      {isEditMode && (
        <>
          <label className="crawl-schedule-modal__checkbox">
            <input
              type="checkbox"
              checked={formValues.active}
              onChange={(event) =>
                setFormValues((current) => ({
                  ...current,
                  active: event.target.checked,
                }))
              }
            />
            <span>{translate('knowledge.crawlScheduleActiveLabel')}</span>
          </label>

          <dl className="crawl-schedule-modal__meta">
            <div>
              <dt>{translate('knowledge.crawlScheduleLastRun')}</dt>
              <dd>{formatTimestamp(schedule.lastRunAt)}</dd>
            </div>
            <div>
              <dt>{translate('knowledge.crawlScheduleNextRun')}</dt>
              <dd>{formatTimestamp(schedule.nextRunAt)}</dd>
            </div>
          </dl>
        </>
      )}
    </LiksDialog>
  );
}
